import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Menu, X } from 'lucide-react';
import WALink from './WALink';

const navLinks = [
  { label: 'Our Story', type: 'scroll', target: 'craft' },
  { label: 'Lookbook', type: 'scroll', target: 'lookbook' },
  { label: 'Collection', type: 'route', target: '/catalogue' },
  { label: 'Bulk Orders', type: 'scroll', target: 'enquiry' },
];

export default function Header() {
  const [elevated, setElevated] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const isCatalogue = location.pathname === '/catalogue';

  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
    // Mount-only listener; `setElevated` is a stable useState setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNav = (link) => {
    setMenuOpen(false);
    if (link.type === 'route') {
      navigate(link.target);
    } else if (!isHome) {
      navigate('/');
      setTimeout(() => document.getElementById(link.target)?.scrollIntoView({ behavior: 'smooth' }), 320);
    } else {
      document.getElementById(link.target)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 bg-[#F5F0E8] border-b transition-all duration-300 ${
        elevated ? 'border-[#C9A84C]/30 shadow-[0_2px_20px_rgba(45,74,45,0.09)]' : 'border-[#D5CCBF]/70'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-[72px]">

        {/* ── Decorative Logo ─────────────────────────── */}
        <Link
          to="/"
          onClick={() => isHome && window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="group flex flex-col items-start gap-[3px] select-none py-1"
          data-testid="header-logo"
        >
          {/* Row 1 — "WEAVES" */}
          <span
            className="font-serif text-[10px] md:text-[11px] uppercase tracking-[0.55em] text-[#2D4A2D] font-semibold leading-none group-hover:tracking-[0.65em] transition-all duration-300"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Weaves
          </span>

          {/* Row 2 — decorative "— of —" */}
          <div className="flex items-center gap-[5px]">
            <div className="h-px w-[14px] bg-[#C9A84C]" />
            <span className="font-sans text-[7px] md:text-[8px] uppercase tracking-[0.3em] text-[#C9A84C] font-semibold leading-none">
              of
            </span>
            <div className="h-px w-[14px] bg-[#C9A84C]" />
          </div>

          {/* Row 3 — "LUCKNOW" */}
          <span
            className="font-serif text-[18px] md:text-[21px] uppercase tracking-[0.1em] text-[#2D4A2D] font-semibold leading-none group-hover:text-[#1A2A1A] transition-colors duration-200"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Lucknow
          </span>
        </Link>

        {/* ── Desktop Nav ──────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => {
            const active = link.type === 'route' && isCatalogue;
            return (
              <button
                key={link.label}
                onClick={() => handleNav(link)}
                className={`relative group font-sans text-[13px] font-medium tracking-wide py-1 transition-colors duration-200 ${
                  active ? 'text-[#C9A84C]' : 'text-[#1A2A1A] hover:text-[#2D4A2D]'
                }`}
              >
                {link.label}
                <span className={`absolute bottom-0 left-0 h-px bg-[#C9A84C] transition-all duration-300 ${
                  active ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </button>
            );
          })}
        </nav>

        {/* ── CTA ──────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <WALink
            source="header"
            data-testid="header-whatsapp-btn"
            className="hidden md:flex items-center gap-2 bg-[#2D4A2D] text-[#F5F0E8] px-5 py-2.5 text-[13px] font-sans font-medium tracking-wide transition-all duration-300 hover:bg-[#1A2A1A] hover:text-[#C9A84C]"
          >
            <MessageCircle size={15} strokeWidth={1.5} />
            Join Community
          </WALink>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-[#1A2A1A] p-2"
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ──────────────────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden bg-[#F5F0E8] border-t border-[#D5CCBF] overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNav(link)}
                  className="text-left font-sans font-medium text-[#1A2A1A] text-sm py-3 border-b border-[#E0D8CC] hover:text-[#2D4A2D] transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <WALink source="mobile_header"
                className="mt-3 bg-[#2D4A2D] text-[#F5F0E8] px-5 py-3.5 text-sm font-sans font-medium text-center flex items-center justify-center gap-2">
                <MessageCircle size={15} strokeWidth={1.5} />
                Join WhatsApp Community
              </WALink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
